import {BillingPeriod, PlanCardModel, PlanId} from "./pricingPlans";
import type {ReferralIntroPrices} from "referral/referralOffer";
import {ComparisonRow} from "./pricingComparison";
import {comparisonShowsPrices} from "./comparedPlans";
import {useMessages} from "i18n";
import {PlanCard, type PlanHeadingLevel} from "./PlanCard";
import {PricingCompareTable} from "./PricingCompareTable";
import {WelcomePriceBand} from "./WelcomePriceBand";
import type {WelcomeBandOccasion, WelcomePriceState} from "./welcomePrice";
import {Columns, Foot, Sheet} from "./PricingContent.styles";

/**
 * The shared pricing body (ticket #45): one pricing sheet (the welcome coupon band, the three paid
 * tier columns and the VAT foot; owner direction "Single Sheet", ui#2426) and the "Compare every
 * feature" table under it, where the Free tier keeps its column. Rendered by BOTH pricing surfaces, the `/pricing`
 * page and the pricing dialog, so the two can never drift. Dumb view per epic #26: the whole
 * view-model (from {@link usePricingPage}) arrives as props.
 *
 * The yearly billing toggle is not rendered since ui#2426 (owner: monthly only for now). Its component,
 * its strings and the yearly amounts in the catalogue are kept for its return; `period` still arrives
 * so the cards and the compare table quote the same one.
 */

export type PricingContentProps = {
	period: BillingPeriod;
	plans: PlanCardModel[];
	/** The welcome price as this reader sees it, and the clock its countdown reads. */
	welcome: WelcomePriceState;
	/** The name the band gives a live run (`welcomeBandOccasion`). */
	welcomeOccasion: WelcomeBandOccasion;
	/** Surface-specific semantic level for each shared plan-card title. */
	planHeadingLevel: PlanHeadingLevel;
	comparisonRows: ComparisonRow[];
	showComparison?: boolean;
	/** The pricing dialog's sheet body scrolls the comparison; the /pricing page hands it to the page scroller (ui#2426). */
	comparisonUsesAncestorScroller?: boolean;
	/**
	 * The id the comparison carries: the `/pricing` page's, so a link can open the page at the free plan's figures,
	 * and the two-plan view opens on Basic and Free there (ui#4018).
	 */
	freePlanAnchorId?: string;
	/**
	 * Card CTA: the tier and the active billing period. The welcome claim token is NOT the card's to
	 * decide: `usePricingPage.pressPlanCta` reads it from the welcome state when the press walks to the
	 * till, so a press made while the answer is still on its way carries what the answer brings.
	 */
	onUpgrade: (planId: PlanId, period: BillingPeriod) => void;
	/**
	 * The referral footnote's figures, in the money these cards are in, or null for no line at all.
	 *
	 * A PROP rather than a hook inside the table: the footnote and the cards must quote one storefront,
	 * and only the view-model that resolved the cards knows which one that is.
	 */
	referralIntro: ReferralIntroPrices | null;
};

export function PricingContent({
	period, plans, welcome, welcomeOccasion, planHeadingLevel, comparisonRows, showComparison = true, comparisonUsesAncestorScroller = false, onUpgrade, referralIntro,
	freePlanAnchorId,
}: PricingContentProps) {
	const {billing} = useMessages().pricing;
	// The sheet holds the paid tiers only (owner decision on ui#2426); Free stays a column of the
	// comparison table below, which is where a reader weighs it against the paid tiers.
	const sheetPlans = plans.filter(plan => plan.id !== "free");
	const columns = plans.map((plan) => ({
		id: plan.id,
		name: plan.name,
		price: plan.amount && plan.per ? `${plan.amount}${plan.per}` : "",
	}));

	return (
		<>
			<Sheet data-testid="pricing-sheet">
				<WelcomePriceBand welcome={welcome} occasion={welcomeOccasion}/>
				<Columns>
					{sheetPlans.map((plan) => (
						<PlanCard key={plan.id} plan={plan} headingLevel={planHeadingLevel}
							onCta={() => onUpgrade(plan.id, period)}/>
					))}
				</Columns>
				<Foot>{billing.vatIncluded}</Foot>
				{period === "MONTHLY" && <Foot>{billing.monthlyTerms}</Foot>}
			</Sheet>
			{showComparison && (
				<PricingCompareTable
					columns={columns}
					rows={comparisonRows}
					highlightPlanId="plus"
					scroll={comparisonUsesAncestorScroller ? "ancestor" : "page"}
					referralIntro={referralIntro}
					freePlanAnchorId={freePlanAnchorId}
					showsPrices={comparisonShowsPrices(plans)}
				/>
			)}
		</>
	);
}
